import { Loader, Center, ActionIcon } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import clsx from 'clsx'
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react'
import Meta from '../common/Meta'
import Header from '../common/components/header/Header'
import Result from '../stories/Result/Result'
import classes from './Matches.module.css'
import { Match, useMatches, useCompetitionRounds } from './api'
import { useParams } from 'react-router'
import { useMemo, useState, Suspense } from 'react'
import dayjs from 'dayjs'
import { Typography } from '../common/typography'
import { useDivisions } from '../ladder/api'
import { useRoundDivisionParams } from '../common/hooks'
import KpiRoundResults from './KpiRoundResults'
import { Dropdown } from '../common/components/dropdown/Dropdown'
import Tabs from '../stories/Tabs/Tabs'

type MatchesTab = 'fixtures' | 'kpi'

const MATCHES_TABS: Array<{ value: MatchesTab; label: string }> = [
  { value: 'fixtures', label: 'Fixtures & Results' },
  { value: 'kpi', label: 'KPI Rank' },
]

interface MatchesListProps {
  competitionId: number
  roundNumber: number | undefined
  division: number | undefined
}

function MatchesList({
  competitionId,
  roundNumber,
  division,
}: MatchesListProps) {
  const { data: matches } = useMatches({
    competition_id: competitionId,
    round_start: roundNumber,
    round_end: roundNumber,
    division,
  })

  return (
    <div className={classes.match_results_container}>
      {matches?.map((match: Match) => (
        <Result
          key={match.match_id}
          match={match}
          baseLink={`/competitions/${competitionId}/matches/${match.match_id}`}
        />
      ))}
    </div>
  )
}

function MatchesListLoader() {
  return (
    <div className={classes.match_results_container}>
      <Center p="xl">
        <Loader color="#0B6B7B" />
      </Center>
    </div>
  )
}

export default function Matches() {
  const { competitionId } = useParams()
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const [activeTab, setActiveTab] = useState<MatchesTab>('fixtures')

  // Fetch rounds at the top level
  const { data: rounds } = useCompetitionRounds({
    competition_id: Number(competitionId),
  })
  const { data: divisions } = useDivisions({
    competition_id: Number(competitionId),
  })

  const { currentRound, currentDivision, setRound, setDivision } =
    useRoundDivisionParams(rounds)

  const selectData = useMemo(
    () =>
      rounds.map((round) => ({
        label: `Round ${round.round_number}`,
        value: round.round_number.toString(),
      })),
    [rounds]
  )

  const divisionData = useMemo(
    () => [
      { label: 'All divisions', value: '' },
      ...(divisions || []).map((division) => ({
        label: division.name,
        value: division.division_id.toString(),
      })),
    ],
    [divisions]
  )

  const currentRoundData = useMemo(
    () => rounds.find((r) => r.round_number === currentRound),
    [rounds, currentRound]
  )

  const minRound = rounds[0]?.round_number ?? null
  const maxRound = rounds[rounds.length - 1]?.round_number ?? null

  const handlePrevRound = () => {
    if (currentRound && minRound && currentRound > minRound) {
      setRound(currentRound - 1)
    }
  }

  const handleNextRound = () => {
    if (currentRound && maxRound && currentRound < maxRound) {
      setRound(currentRound + 1)
    }
  }

  return (
    <div className={classes.container}>
      <Meta title="Matches" />
      <Header title="Matches" />
      <div
        className={clsx(classes.matches_wrapper, {
          [classes.matches_wrapper_mobile]: !isDesktop,
        })}
      >
        <div className={classes.matches}>
          <div
            className={clsx(classes.matches_header, {
              [classes.matches_header_mobile]: !isDesktop,
            })}
          >
            {isDesktop ? (
              <Typography
                level={2}
                fw={500}
                lh="125"
                fc={{ light: 'black', dark: 'gray-2' }}
              >
                Fixtures & Results
              </Typography>
            ) : (
              <Tabs
                items={MATCHES_TABS}
                value={activeTab}
                onChange={(v) => setActiveTab(v as MatchesTab)}
                isSmall
              />
            )}
            <div className={classes.filtersContainer}>
              <Dropdown
                placeholder="Division"
                value={currentDivision?.toString() ?? ''}
                onChange={(value) => setDivision(value ? Number(value) : null)}
                data={divisionData}
                rightSection={
                  <IconChevronDown className={classes.dropdownIcon} />
                }
                styles={{ root: { maxWidth: '10rem' } }}
              />

              <div className={classes.roundContainer}>
                <ActionIcon
                  variant="outline"
                  color="gray"
                  className={classes.action_icon}
                  onClick={handlePrevRound}
                  disabled={
                    !currentRound || !minRound || currentRound <= minRound
                  }
                >
                  <IconChevronLeft size={20} />
                </ActionIcon>

                <Dropdown
                  placeholder="Round"
                  value={currentRound?.toString() ?? null}
                  onChange={(value) => setRound(value ? Number(value) : null)}
                  data={selectData}
                  rightSection={
                    <IconChevronDown className={classes.dropdownIcon} />
                  }
                  styles={{ root: { maxWidth: '7rem' } }}
                />

                <ActionIcon
                  variant="outline"
                  color="gray"
                  className={classes.action_icon}
                  onClick={handleNextRound}
                  disabled={
                    !currentRound || !maxRound || currentRound >= maxRound
                  }
                >
                  <IconChevronRight size={20} />
                </ActionIcon>
              </div>
            </div>
          </div>

          <div
            className={clsx(classes.data_wrapper, {
              [classes.mobile_container]: !isDesktop,
            })}
          >
            <div className={classes.round_header}>
              <Typography
                fs="sm"
                fw={500}
                fc={{ light: 'black', dark: 'gray-3' }}
                lh="130"
              >
                {currentRound ? `Round ${currentRound}` : 'All rounds'}
              </Typography>
              <Typography
                fs="sm"
                fw={500}
                fc={{ light: 'black', dark: 'gray-3' }}
                lh="130"
                ta="right"
              >
                {currentRoundData
                  ? `${dayjs(currentRoundData.round_start).format('D MMM')} - ${dayjs(currentRoundData.round_start).add(6, 'day').format('D MMM')}`
                  : ''}
              </Typography>
            </div>

            <div className={classes.matchesWrapper}>
              {(isDesktop || activeTab === 'fixtures') && (
                <div className={classes.matches_container}>
                  <Suspense fallback={<MatchesListLoader />}>
                    <MatchesList
                      competitionId={Number(competitionId)}
                      roundNumber={currentRound || undefined}
                      division={currentDivision ?? undefined}
                    />
                  </Suspense>
                </div>
              )}
              {(isDesktop || activeTab === 'kpi') && (
                <div
                  className={clsx(classes.kpiRoundResultsContainer, {
                    [classes.kpiRoundResultsContainerMobile]: !isDesktop,
                  })}
                >
                  <KpiRoundResults
                    competitionId={Number(competitionId)}
                    roundNumber={currentRound}
                    roundStart={currentRoundData?.round_start}
                    division={currentDivision ?? undefined}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
